"use client";

import { Button } from "@/components";
import styles from "@/styles/containers/evaluate.module.scss";
import { useEffect, useState } from "react";

import Star from "/public/Star.png";
import StarSuccess from "/public/StarSuccess.png";

import { ArrowLeft, StarBlank, StarGold, StarRed } from "@/components/Icons";
import { Input } from "@/components/Input";
import classNames from "classnames/bind";
import { useTranslations } from "next-intl";
import Image from "next/image";
const css = classNames.bind(styles);

const types = {
  SERVICE: "service",
  ORDER: "order",
};

const steps = {
  EVALUATE_SERVICE: "EVALUATE_SERVICE",
  EVALUATE_ORDER: "EVALUATE_ORDER",
  OBSERVATION: "OBSERVATION",
  FINISHED: "FINISHED",
};

export default function Evaluate({ onClose }) {
  const t = useTranslations();

  const serviceTags = [
    t("education"),
    t("patience"),
    t("communication"),
    t("speed"),
    t("sympathy"),
    t("availability"),
    t("suggestions"),
  ];

  const orderTags = [
    t("flavor"),
    t("seasoning"),
    t("costBenefit"),
    t("appearance"),
    t("quantity"),
    t("packaging"),
    t("temperature"),
  ];

  const [payload, setPayload] = useState({
    service: {
      evaluate: null,
      items: [],
    },
    order: {
      evaluate: null,
      items: [],
    },
    observation: null,
  });
  const [disabled, setDisabled] = useState(false);
  const [step, setStep] = useState(steps.EVALUATE_ORDER);

  const isSelected = (type, tag) => {
    return payload[type].items.indexOf(tag) > -1;
  };

  const setTags = (type, tag) => {
    let tagsArr = payload[type].items;
    const index = tagsArr.indexOf(tag);

    if (index > -1) tagsArr.splice(tagsArr.indexOf(tag), 1);
    else tagsArr = [...tagsArr, tag];

    setPayload((payload) => ({
      ...payload,
      [type]: {
        ...payload[type],
        items: tagsArr,
      },
    }));
  };

  useEffect(() => {
    const serviceRating = payload.service.evaluate;
    const orderRating = payload.order.evaluate;

    if (
      (step === steps.EVALUATE_SERVICE &&
        (!serviceRating || payload.service.items.length === 0)) ||
      (step === steps.EVALUATE_ORDER &&
        (!orderRating || payload.order.items.length === 0))
    )
      return setDisabled(true);

    setDisabled(false);
  }, [payload, step]);

  const prev = () => {
    if (step === steps.EVALUATE_ORDER) onClose();
    if (step === steps.EVALUATE_SERVICE) setStep(steps.EVALUATE_ORDER);
    if (step === steps.OBSERVATION) setStep(steps.EVALUATE_SERVICE);
    if (step === steps.FINISHED) setStep(steps.OBSERVATION);
  };

  const next = () => {
    if (step === steps.EVALUATE_ORDER) setStep(steps.EVALUATE_SERVICE);
    if (step === steps.EVALUATE_SERVICE) setStep(steps.OBSERVATION);
    if (step === steps.OBSERVATION) setStep(steps.FINISHED);
  };

  const getLabel = () => {
    const labels = {
      [steps.EVALUATE_SERVICE]: t("rateTheService"),
      [steps.EVALUATE_ORDER]: t("rateTheOrder"),
      [steps.OBSERVATION]: t("anyObservations"),
      [steps.FINISHED]: t("weWillConsiderYourExperience"),
    };

    return labels[step];
  };

  return (
    <div className={styles.evaluateContainer}>
      {step === steps.FINISHED ? (
        <div className={css({ header: true, finished: true })}>
          <div className={styles.star}>
            <Image src={StarSuccess} alt="" width={75} height={75} />
          </div>
          <p className={styles.title}>{t("thankYouForYourAttention")}</p>
          <p className={styles.label}>{getLabel()}</p>
        </div>
      ) : (
        <div className={css({ header: true })}>
          <Image src={Star} alt="" width={75} height={75} />
          <p className={styles.title}>{t("rateYourExperienceWithUs")}</p>
          <p className={styles.label}>{getLabel()}</p>
          {[steps.EVALUATE_ORDER, steps.EVALUATE_SERVICE].includes(step) && (
            <div className={styles.hairline} />
          )}
        </div>
      )}

      <div className={styles.categories}>
        {step === steps.EVALUATE_ORDER && (
          <div className={styles.category}>
            <div className={styles.stars}>
              {[1, 2, 3, 4, 5].map((value) => {
                const rating = payload?.order?.evaluate;

                return (
                  <div
                    className={css({
                      star: true,
                      good: rating >= value && rating > 2,
                      bad: rating >= value && rating <= 2,
                    })}
                    key={value}
                    onClick={() => {
                      setPayload((payload) => ({
                        ...payload,
                        [types.ORDER]: {
                          ...payload[types.ORDER],
                          evaluate: value,
                        },
                      }));
                    }}
                  >
                    {rating >= value && rating > 2 && <StarGold />}
                    {rating >= value && rating <= 2 && <StarRed />}
                    {!(rating >= value) && <StarBlank />}
                  </div>
                );
              })}
            </div>

            {payload?.order?.evaluate && (
              <>
                <p className={styles.sublabel}>
                  {payload?.order?.evaluate <= 2
                    ? t("whatCanWeImprove")
                    : t("whatDidYouLike")}
                </p>

                <div className={styles.tags}>
                  {orderTags.map((tag) => {
                    return (
                      <div
                        className={css({
                          tag: true,
                          selected: isSelected(types.ORDER, tag),
                        })}
                        key={tag}
                        onClick={() => setTags(types.ORDER, tag)}
                      >
                        {tag}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {step === steps.EVALUATE_SERVICE && (
          <div className={styles.category}>
            <div className={styles.stars}>
              {[1, 2, 3, 4, 5].map((value) => {
                const rating = payload?.service?.evaluate;

                return (
                  <div
                    className={css({
                      star: true,
                      good: rating >= value && rating > 2,
                      bad: rating >= value && rating <= 2,
                    })}
                    key={value}
                    onClick={() => {
                      setPayload((payload) => ({
                        ...payload,
                        [types.SERVICE]: {
                          ...payload[types.SERVICE],
                          evaluate: value,
                        },
                      }));
                    }}
                  >
                    {rating >= value && rating > 2 && <StarGold />}
                    {rating >= value && rating <= 2 && <StarRed />}
                    {!(rating >= value) && <StarBlank />}
                  </div>
                );
              })}
            </div>

            {payload?.service?.evaluate && (
              <>
                <p className={styles.sublabel}>
                  {payload?.service?.evaluate <= 2
                    ? t("whatCanWeImprove")
                    : t("whatDidYouLike")}
                </p>
                <div className={styles.tags}>
                  {serviceTags.map((tag) => {
                    return (
                      <div
                        className={css({
                          tag: true,
                          selected: isSelected(types.SERVICE, tag),
                        })}
                        key={tag}
                        onClick={() => setTags(types.SERVICE, tag)}
                      >
                        {tag}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {step === steps.OBSERVATION && (
        <div className={styles.enterCode}>
          <Input
            autoFocus
            value={payload?.observation || ""}
            placeholder={t("note")}
            onChange={(e) =>
              setPayload((prev) => ({ ...prev, observation: e.target.value }))
            }
          />
        </div>
      )}

      <div className={styles.footer}>
        {step !== steps.FINISHED ? (
          <>
            <div className="d-flex align-center justify-between w-100">
              <Button design="secondary" onClick={prev}>
                <ArrowLeft />
                {t("back")}
              </Button>
              {step === steps.OBSERVATION && !payload?.observation ? (
                <Button design="default" onClick={next}>
                  {t("skipAndFinish")}
                </Button>
              ) : (
                <Button onClick={next} disabled={disabled}>
                  {t("next")}
                </Button>
              )}
            </div>
          </>
        ) : (
          <Button onClick={onClose}>{t("complete")}</Button>
        )}
      </div>
    </div>
  );
}
